import React from 'react';
import { useTranslation, useExperiments } from '@wix/yoshi-flow-editor';
import { Badge, BadgeSkin } from '@wix/design-system';
import { st, classes } from './StatusBadgeBass.st.css';
import { Subscription, SubscriptionStatus } from '@wix/ambassador-billing-v1-subscription/types';
import {
  hasScheduledSkip,
  isCurrentlySkipping,
  isInFreeTrial,
  isPendingCancellationSubscription,
} from '../domainUtils';
import { Experiments } from '../../../Experiments';

const activeStatuses = [SubscriptionStatus.ACTIVE];

const statusTranslations: Record<SubscriptionStatus, string> = {
  [SubscriptionStatus.UNKNOWN]: 'app.subscription-status.undefined',
  [SubscriptionStatus.DRAFT]: 'app.subscription-status.draft',
  [SubscriptionStatus.PENDING]: 'app.subscription-status.pending',
  [SubscriptionStatus.ACTIVE]: 'app.subscription-status.active',
  [SubscriptionStatus.ENDED]: 'app.subscription-status.expired',
  [SubscriptionStatus.CANCELED]: 'app.subscription-status.canceled',
  [SubscriptionStatus.PAUSED]: 'app.subscription-status.suspended',
};

export interface StatusFlags {
  enableAdvancedPauseResume?: boolean;
  enableSkipSubscriptions?: boolean;
}

export const getStatusDisplay = (
  subscription: Subscription,
  { enableAdvancedPauseResume, enableSkipSubscriptions }: StatusFlags = {},
) => {
  const skipping = !!enableSkipSubscriptions && isCurrentlySkipping(subscription);
  const skipScheduled = !!enableSkipSubscriptions && hasScheduledSkip(subscription);
  const inFreeTrial = isInFreeTrial(subscription);
  const active = inFreeTrial || activeStatuses.indexOf(subscription.status!) > -1 || skipScheduled;
  const skin: BadgeSkin = skipping ? 'warningLight' : active ? 'neutralSuccess' : 'neutralLight';

  const translationKey = (() => {
    if (skipping) {
      return 'app.subscription-status.activeSkippingOrder';
    }
    if (skipScheduled) {
      return 'app.subscription-status.activeSkipScheduled';
    }
    if (
      [SubscriptionStatus.ACTIVE, SubscriptionStatus.PENDING].indexOf(
        subscription.status ?? SubscriptionStatus.UNKNOWN,
      ) > -1 &&
      isPendingCancellationSubscription(subscription)
    ) {
      return 'app.subscription-status.pending-cancellation';
    }
    if (inFreeTrial) {
      return 'app.subscription-status.free-trial';
    }
    if (subscription.status === SubscriptionStatus.PAUSED && enableAdvancedPauseResume) {
      return 'app.subscription-status.paused';
    }
    return statusTranslations[subscription.status! as SubscriptionStatus];
  })();

  return { translationKey, skin };
};

export const getStatusTextTranslationKey = (subscription: Subscription, flags: StatusFlags = {}) =>
  getStatusDisplay(subscription, flags).translationKey;

type Props = {
  subscription: Subscription;
  showTooltip?: boolean;
};

export const StatusBadgeBass = ({ subscription, showTooltip = true }: Props) => {
  const { t } = useTranslation();
  const { experiments } = useExperiments();
  const enableAdvancedPauseResume = experiments.enabled(Experiments.ADVANCED_PAUSE_RESUME);
  const enableSkipSubscriptions = experiments.enabled(Experiments.SKIP_SUBSCRIPTIONS_MEMBERS_AREA);
  const { translationKey, skin } = getStatusDisplay(subscription, {
    enableAdvancedPauseResume,
    enableSkipSubscriptions,
  });

  return (
    <Badge className={st(classes.root)} uppercase={false} skin={skin} dataHook="status-badge" showTooltip={showTooltip}>
      {t(translationKey)}
    </Badge>
  );
};
